<template>
    <!-- reserve-scrollbar-gutter: this modal's content (the pig-tally chart especially) can already
         sit close to viewport height, and opening a date picker's calendar on top of it is enough to
         cross into needing the overlay's own scroll - reserving the gutter unconditionally here keeps
         that from visibly shifting the dialog a few pixels when it happens. -->
    <Dialog v-model="showModal" :disabled="isLoading || isRecalculating" class="sm:max-w-[720px]" reserve-scrollbar-gutter>
        <DialogHeader>
            <DialogTitle>
                {{ isEditMode ? $t('views.monitoring.productionGroups.modals.manageProductionGroup.editTitle') : $t('views.monitoring.productionGroups.modals.manageProductionGroup.createTitle') }}
            </DialogTitle>
            <VisuallyHidden>
                <DialogDescription class="pt-4">{{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.description') }}</DialogDescription>
            </VisuallyHidden>
        </DialogHeader>

        <!-- Farm · Barn context, then the Section nested one level under it — grouped into one card so
             the pair reads as a single "you are editing this, here's where it lives" unit rather than
             two loose floating lines, with the Section carrying the visual weight since it's the thing
             actually being edited. -->
        <div class="mt-3 rounded-lg bg-gray-50 px-3 py-2.5">
            <p class="text-xs font-medium text-gray-400 uppercase tracking-wide">{{ farmName }} · {{ barnName }}</p>
            <p class="mt-1 flex items-center gap-1.5">
                <CornerDownRight class="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
                <span class="text-base font-bold text-gray-900">{{ sectionName }}</span>
            </p>
        </div>

        <form class="mt-4 space-y-3" @submit.prevent="handleSubmit">
            <!-- Name field is first so Dialog auto-focus lands here, not on the DatePicker. -->
            <CustomInput v-model:value="state.name" required input-type="text" :rules="[]" :label="$t('views.monitoring.productionGroups.modals.manageProductionGroup.fields.name')" />
            <CustomTextArea
                v-model:value="state.note"
                :required="false"
                :rows="2"
                :maxlength="NOTE_MAX_LENGTH"
                :rules="[]"
                :label="$t('views.monitoring.productionGroups.modals.manageProductionGroup.fields.note')"
            />
            <div class="grid grid-cols-2 gap-x-4">
                <!-- Start required and capped at today (or the existing end date in edit mode); end
                     optional and capped at today in *both* modes. Setting the end to today is what
                     closing a Production Group means here, so a future end date would be a
                     Production Group that is neither open nor closed. No lower bound on the calendar
                     itself - a historic/colliding start is caught by the overlap check below instead
                     of being silently unselectable, so the farmer gets a reason rather than a wall.
                     That check only sees groups inside `timelineRange` (3 months back from whatever
                     start is currently typed), which re-centers as the field changes, so it still
                     catches a collision near wherever the farmer actually lands - just not one far
                     outside that window while they're mid-edit. -->
                <DatePicker v-model:value="state.startDate" required :max="startDateMax" :label="$t('views.monitoring.productionGroups.modals.manageProductionGroup.fields.startDate')" />
                <DatePicker v-model:value="state.endDate" resetable :min="state.startDate" :max="todayIso" :label="$t('views.monitoring.productionGroups.modals.manageProductionGroup.fields.endDate')" />
            </div>
        </form>

        <!-- Which cameras the Section carries depends on the date being asked about, so this follows
             the start date in the form rather than the Production Group's saved one. -->
        <SectionCamerasReadout class="mt-3" :section-id="sectionId" :date="toDateOnly(state.startDate)" />

        <ProductionGroupTimelineChart
            class="mt-4"
            :tally="pigTally"
            :production-groups="timelineProductionGroups"
            :range="timelineRange"
            :form-start="toDateOnly(state.startDate) ?? ''"
            :form-end="toDateOnly(state.endDate) ?? ''"
            :edited-production-group-id="isEditMode ? target?.productionGroupId ?? null : null"
            :suggested-start="suggestedStart"
            :suggestion-conflicts="suggestedStartConflicts"
            :is-loading="isTimelineLoading"
            :has-error="hasTimelineError"
            @use-suggested-start="handleUseSuggestedStart"
            @retry="refreshTimeline"
        />

        <!-- End date before start date: immediate client-side feedback, no API call. -->
        <p v-if="dateRangeInvalid" class="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.errors.endBeforeStart') }}
        </p>
        <p v-else-if="overlappingProductionGroup" class="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.errors.overlapsExisting', { name: overlappingProductionGroup.name }) }}
        </p>
        <p v-if="startHasPigs" class="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.warnings.nonZeroStart') }}
        </p>

        <DialogFooter class="mt-2" :class="isEditMode ? 'sm:justify-between' : ''">
            <!-- Left-side actions: only meaningful once the Production Group exists, so both are
                 hidden in create mode. Kept visually secondary (plain text, no fill) and on the
                 opposite side from Cancel/Save so neither reads as a competing primary action. -->
            <div v-if="isEditMode" class="flex items-center gap-4">
                <!-- Delete: a real removal of the Production Group row, distinct from "closing" one by
                     setting its end date. Only offered by callers that actually need hard delete
                     (Groups overview) - Active groups has no such affordance today. -->
                <button
                    v-if="allowDelete"
                    type="button"
                    class="text-sm font-medium text-red-600 transition-colors hover:text-red-700 disabled:cursor-default disabled:opacity-50"
                    :disabled="isLoading || isRecalculating"
                    @click="target?.productionGroupId != null && emit('delete', target.productionGroupId)"
                >
                    {{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.buttons.delete') }}
                </button>
                <!-- On-demand Forced Recompute. -->
                <button
                    type="button"
                    class="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-primary-700 transition-colors hover:text-primary-800 disabled:cursor-default disabled:opacity-50"
                    :disabled="isLoading || isRecalculating"
                    :title="$t('views.monitoring.productionGroups.modals.manageProductionGroup.buttons.recompute')"
                    @click="handleForceRecompute"
                >
                    <Icon :src="IconSource.RefreshActivities" :class="{ 'animate-spin': isRecalculating }" style="width: 15px; height: 15px" />
                    <span>{{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.buttons.recompute') }}</span>
                </button>
            </div>
            <div class="flex flex-col-reverse gap-2 sm:flex-row sm:gap-x-2">
                <DialogClose as-child>
                    <CustomButton :disabled="isLoading || isRecalculating" color-preset="white" class="w-auto min-w-28">
                        {{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.buttons.cancel') }}
                    </CustomButton>
                </DialogClose>
                <CustomButton :loading="isLoading" :disabled="!isFormValid || isRecalculating || (isEditMode && !isDirty)" class="w-auto min-w-28" @click="handleSubmit">
                    {{ isEditMode ? $t('views.monitoring.productionGroups.modals.manageProductionGroup.buttons.save') : $t('views.monitoring.productionGroups.modals.manageProductionGroup.buttons.create') }}
                </CustomButton>
            </div>
        </DialogFooter>
    </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useQueryClient, type QueryKey } from '@tanstack/vue-query';
import { VisuallyHidden } from 'radix-vue';
import { format, parseISO } from 'date-fns';
import { useToast } from 'vue-toastification';
import { CornerDownRight } from 'lucide-vue-next';
import CustomButton from '@/components/ui/CustomButton.vue';
import CustomInput from '@/components/ui/CustomInput.vue';
import CustomTextArea from '@/components/ui/CustomTextArea.vue';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import DatePicker from '@/components/ui/DatePicker.vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { i18n } from '@/plugins/internationalization/i18n';
import { queryKeys as productionGroupQueryKeys, useProductionGroupTimeline, useBackfillPigTallySeries, type ProductionGroupTimelineRequest } from '@/plugins/store/actions/queries/production-groups';
import { IconSource, type ProductionGroup } from '@/types';
import SectionCamerasReadout from './SectionCamerasReadout.vue';
import ProductionGroupTimelineChart from './ProductionGroupTimelineChart.vue';
import {
    findProductionGroupOverlap,
    findSuggestedProductionGroupStart,
    getProductionGroupTimelineRange,
    isProductionGroupRangeTooShort,
    pigTallyAtDate,
    productionGroupIntersectsTimeline,
    type ProductionGroupTimelineRange,
} from './productionGroupTimeline';

// Shared create/adjust/close modal for a single Section's Production Group - opened from Active
// groups (one row per physical Section, already resolved) and from Groups overview (resolved from
// the clicked group, or from a Barn + a chosen Section for create). Mode is derived from
// `target.productionGroupId`: null → create, set → edit. Carries only the date fields the PRD scopes
// here (name / animal group / pig count are out of scope for the field set, though animal group is
// still derived and sent server-side); Groups overview needs actual delete on top (see `allowDelete`),
// which Active groups doesn't expose.
const NOTE_MAX_LENGTH = 1000;

// The minimum a caller must resolve before opening this modal. Deliberately narrower than Active
// groups' own `SectionRow` (which structurally satisfies this and can be passed straight through) so
// Groups overview isn't forced to build a full SectionRow just to open it.
export type ManageProductionGroupTarget = {
    id: number;
    sectionName: string;
    farmName: string;
    barnName: string;
    barnId: number;
    productionGroupId: number | null;
};

const props = defineProps<{
    // The Section (+ its active Production Group, if any) the manage button was clicked on. Null
    // while no target is selected (modal closed).
    target: ManageProductionGroupTarget | null;
    // The active Production Group for the target, when one exists — supplies the existing name + dates in edit mode.
    productionGroup: ProductionGroup | null;
    // Whether the Delete action is offered in edit mode. Off by default (Active groups has no hard
    // delete today) - Groups overview passes true, since it's replacing modals that did.
    allowDelete?: boolean;
    // Extra query-key roots to invalidate after a successful save/recompute, on top of the Production
    // Group list every caller shares. Each page owns the list for whatever it caches locally keyed by
    // Section - this modal has no way to know which caches a given caller's page populated.
    extraInvalidateQueryKeyRoots?: QueryKey[];
}>();

const emit = defineEmits<{
    delete: [productionGroupId: number];
}>();

const showModal = defineModel<boolean>('modelValue');

const queryClient = useQueryClient();
const toast = useToast();

const isLoading = ref(false);
// True while a Forced Recompute call is in flight (edit mode only, triggered by its own button).
const isRecalculating = ref(false);

const isEditMode = computed(() => props.target?.productionGroupId != null);
const sectionName = computed(() => props.target?.sectionName ?? '');
const farmName = computed(() => props.target?.farmName ?? '');
const barnName = computed(() => props.target?.barnName ?? '');
const todayIso = computed(() => format(new Date(), 'yyyy-MM-dd'));

interface ManageState {
    name: string;
    note: string;
    startDate: string;
    endDate: string;
}

function getInitialState(): ManageState {
    return {
        name: '',
        note: '',
        startDate: '',
        endDate: '',
    };
}

const state = ref<ManageState>(getInitialState());

// Snapshot of state as seeded when the modal opened — Save stays disabled until something in the
// form actually differs, so it can't be used as a no-op stand-in for the Recompute button.
const originalState = ref<ManageState>(getInitialState());
const initialCreateStartDate = ref<string | null>(null);
const hasAppliedSuggestedStart = ref(false);

// Centered on the form's own start date (3 months back, 4 forward) once one is picked, so the chart
// follows whichever Production Group is actually being created/edited instead of a fixed "last six
// months" window that could leave an older one - or the one currently being edited - off-screen
// entirely. Falls back to today while the field is still blank (fresh create mode). This is the
// chart's own x-axis range - it's allowed to reach past today so a future-dated plan still draws.
const timelineRange = computed<ProductionGroupTimelineRange>(() => {
    const start = toDateOnly(state.value.startDate);
    return getProductionGroupTimelineRange(start ? parseISO(start) : new Date());
});

// What's actually queried from the backend: both the tally and the Production Group reads reject a
// future `to` (there's nothing to return past today), so this clamps the chart's own forward-looking
// range down to today for the fetch while leaving `timelineRange` itself - the chart's x-axis - free
// to extend further, drawing as empty space the same way the Active groups tally chart already does.
const queryRange = computed<ProductionGroupTimelineRange>(() => ({
    from: timelineRange.value.from,
    to: timelineRange.value.to < todayIso.value ? timelineRange.value.to : todayIso.value,
}));

const timelineRequest = computed<ProductionGroupTimelineRequest | null>(() =>
    showModal.value && props.target
        ? {
              sectionId: props.target.id,
              barnId: props.target.barnId,
              from: queryRange.value.from,
              to: queryRange.value.to,
          }
        : null
);
// useBackfillPigTallySeries (not active-groups' usePigTallySeries) deliberately - that one's backend
// caps lookback at 6 months before today for its own KPI-detail caller, so a group whose own window
// is older than that (e.g. editing one from last year) would 400 the whole modal. The backfill-business
// endpoint has no such floor and already clamps `to` to today itself, matching queryRange's own clamp.
const pigTallyRange = computed<{ from: string; to: string } | null>(() => (timelineRequest.value ? { from: queryRange.value.from, to: queryRange.value.to } : null));
const sectionId = computed(() => props.target?.id ?? null);
const { data: pigTallyData, isLoading: isPigTallyLoading, isError: isPigTallyError, refetch: refetchPigTally } = useBackfillPigTallySeries(sectionId, pigTallyRange);
const {
    data: productionGroupTimelineData,
    isLoading: isProductionGroupTimelineLoading,
    isError: isProductionGroupTimelineError,
    refetch: refetchProductionGroupTimeline,
} = useProductionGroupTimeline(timelineRequest);
const pigTally = computed(() => pigTallyData.value ?? []);
const allTimelineProductionGroups = computed(() => productionGroupTimelineData.value ?? []);
const timelineProductionGroups = computed(() => allTimelineProductionGroups.value.filter((productionGroup) => productionGroupIntersectsTimeline(productionGroup, timelineRange.value)));
const isTimelineLoading = computed(() => isPigTallyLoading.value || isProductionGroupTimelineLoading.value);
const hasTimelineError = computed(() => isPigTallyError.value || isProductionGroupTimelineError.value);
const rawSuggestedStart = computed(() => findSuggestedProductionGroupStart(pigTally.value));
const suggestedStartConflicts = computed(
    () =>
        rawSuggestedStart.value !== null &&
        findProductionGroupOverlap({ start: rawSuggestedStart.value, end: null, productionGroups: allTimelineProductionGroups.value, today: todayIso.value, ignoredProductionGroupId: null }) !== null
);
const suggestedStart = computed(() => (suggestedStartConflicts.value ? null : rawSuggestedStart.value));

// True when the end date is set and falls before the start date.
const dateRangeInvalid = computed(() => {
    const start = toDateOnly(state.value.startDate);
    return start !== null && isProductionGroupRangeTooShort(start, toDateOnly(state.value.endDate));
});

// Compares normalized values, not raw ISO strings — the DatePicker round-trips timestamps that
// differ from the seeded value even when the calendar day hasn't changed.
function toDateOnly(iso: string): string | null {
    return iso ? format(parseISO(iso), 'yyyy-MM-dd') : null;
}

// The inverse: DatePicker's v-model (and its display formatting) is a timestamp, not a bare date.
// A date-only "yyyy-MM-dd" fed straight into it is parsed as UTC midnight and can render as the
// wrong calendar day in negative-offset timezones, so every date-only source (tally dates, the
// suggested start) must go through here before reaching `state.startDate`.
function toPickerValue(dateOnly: string): string {
    return parseISO(dateOnly).toISOString();
}

// An existing Production Group may be moved earlier, but never start after its current end date.
// Create mode remains capped at today, because its end is still optional and user-entered.
const startDateMax = computed(() => {
    const end = isEditMode.value ? toDateOnly(state.value.endDate) : null;
    return end && end < todayIso.value ? end : todayIso.value;
});

// Guard the picker constraint at submit time too, because a model can be set programmatically or
// by typed input. Production Groups cannot start in the future.
const startDateInFuture = computed(() => {
    const startDate = toDateOnly(state.value.startDate);
    return startDate != null && startDate > todayIso.value;
});

const overlappingProductionGroup = computed(() => {
    const start = toDateOnly(state.value.startDate);
    if (!start || dateRangeInvalid.value) return null;

    return findProductionGroupOverlap({
        start,
        end: toDateOnly(state.value.endDate),
        productionGroups: allTimelineProductionGroups.value,
        today: todayIso.value,
        ignoredProductionGroupId: isEditMode.value ? props.target?.productionGroupId ?? null : null,
    });
});

// The tally warning is intentionally advisory: a farmer can knowingly begin analysis mid-batch.
// The suggested start is the first non-zero day after an empty Section, so it is explicitly valid.
const startHasPigs = computed(() => {
    const start = toDateOnly(state.value.startDate);
    return start !== null && start !== suggestedStart.value && (pigTallyAtDate(pigTally.value, start) ?? 0) > 0;
});

// Name and start date required; start cannot be future-dated, end date (when set) must not precede
// start date, and the overlap/suggestion data (pig tally + Production Group timeline, both folded into
// `isTimelineLoading`/`hasTimelineError`) must have loaded cleanly — overlap prevention and the
// non-zero-start warning are both meaningless against stale or missing data.
const isFormValid = computed(
    () =>
        state.value.name.trim().length > 0 &&
        state.value.startDate.length > 0 &&
        !startDateInFuture.value &&
        !dateRangeInvalid.value &&
        !isTimelineLoading.value &&
        !hasTimelineError.value &&
        !overlappingProductionGroup.value
);

const isDirty = computed(
    () =>
        state.value.name.trim() !== originalState.value.name.trim() ||
        state.value.note.trim() !== originalState.value.note.trim() ||
        toDateOnly(state.value.startDate) !== toDateOnly(originalState.value.startDate) ||
        toDateOnly(state.value.endDate) !== toDateOnly(originalState.value.endDate)
);

// Refreshes the Production Group list every caller shares, plus whatever else the calling page
// cached locally for this Section (its `extraInvalidateQueryKeyRoots` - e.g. Active groups' live
// KPIs and series charts).
function refreshSectionData() {
    return Promise.all([
        queryClient.invalidateQueries({ queryKey: productionGroupQueryKeys.getProductionGroupsRoot }),
        ...(props.extraInvalidateQueryKeyRoots ?? []).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    ]);
}

async function handleSubmit() {
    if (isLoading.value || isRecalculating.value || !props.target || !isFormValid.value || (isEditMode.value && !isDirty.value)) {
        return;
    }

    isLoading.value = true;
    try {
        const start = format(parseISO(state.value.startDate), 'yyyy-MM-dd');
        const end = state.value.endDate ? format(parseISO(state.value.endDate), 'yyyy-MM-dd') : null;
        // The column is nullable and an empty note is the absence of one, so blank text is sent as
        // null rather than storing an empty string alongside it.
        const note = state.value.note.trim() || null;

        if (isEditMode.value) {
            await store.dispatch(ActionType.EditProductionGroups, {
                options: { skipHandleError: true },
                form: {
                    production_groups: [
                        {
                            id: props.target.productionGroupId!,
                            name: state.value.name.trim(),
                            note,
                            start,
                            end,
                            is_favorite: props.productionGroup?.is_favorite ?? false,
                        },
                    ],
                },
            });

            await forceRecompute(props.target.productionGroupId!);
        } else {
            // A new Production Group has no derived figures until Nexus has rebuilt its Production
            // Cycle. Wait for that targeted recompute before refreshing the overview, so its
            // first read cannot show a partly-created period with stale/null calculations.
            const createdProductionGroup: ProductionGroup = await store.dispatch(ActionType.CreateProductionGroup, {
                options: { skipHandleError: true },
                form: {
                    location_id: props.target.id,
                    name: state.value.name.trim(),
                    note,
                    start,
                    end,
                    is_favorite: false,
                },
            });

            await forceRecompute(createdProductionGroup.id);
        }

        await refreshSectionData();

        showModal.value = false;
        toast.success(
            isEditMode.value
                ? i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.successMessages.edited')
                : i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.successMessages.created')
        );
    } catch (err) {
        const errorMessage =
            (err as any)?.data?.conflicts?.[0]?.conflict_message || (err as any)?.data?.message || i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.errors.couldNotSave');
        toast.error(errorMessage);
    } finally {
        isLoading.value = false;
    }
}

// Forced Recompute: on-demand, synchronous, single-cycle recompute of DLM + Water Norm figures.
// Independent of Save — runs against whatever is currently persisted for this Production Group.
async function handleForceRecompute() {
    if (isRecalculating.value || isLoading.value || props.target?.productionGroupId == null) {
        return;
    }

    isRecalculating.value = true;
    try {
        await forceRecompute(props.target.productionGroupId);
        await refreshSectionData();
        toast.success(i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.successMessages.recomputed'));
    } catch {
        toast.error(i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.errors.recomputeFailed'));
    } finally {
        isRecalculating.value = false;
    }
}

function forceRecompute(productionGroupId: number) {
    return store.dispatch(ActionType.ForceRecomputeProductionGroup, {
        options: { skipHandleError: true },
        form: { productionGroupId },
    });
}

function handleUseSuggestedStart(start: string) {
    state.value.startDate = toPickerValue(start);
}

function refreshTimeline() {
    return Promise.all([refetchPigTally(), refetchProductionGroupTimeline()]);
}

// Create mode uses the latest observed empty-to-occupied transition as a helpful default. It runs
// only once and only while the untouched blank value remains, so a late query result cannot replace
// a date the farmer already entered. Edit mode always remains an explicit helper action instead.
function applySuggestedStartIfAppropriate(start: string | null) {
    if (!showModal.value || !start || isEditMode.value || hasAppliedSuggestedStart.value || toDateOnly(state.value.startDate) !== initialCreateStartDate.value) return;

    state.value.startDate = toPickerValue(start);
    hasAppliedSuggestedStart.value = true;
}

// Seed the form from the active Production Group (edit) or defaults (create) each time the modal opens.
watch(showModal, (value) => {
    if (value) {
        hasAppliedSuggestedStart.value = false;
        state.value = isEditMode.value
            ? {
                  name: props.productionGroup?.name ?? '',
                  note: props.productionGroup?.note ?? '',
                  startDate: props.productionGroup?.start ? new Date(props.productionGroup.start).toISOString() : new Date().toISOString(),
                  endDate: props.productionGroup?.end ? new Date(props.productionGroup.end).toISOString() : '',
              }
            : getInitialState();
        originalState.value = { ...state.value };
        initialCreateStartDate.value = toDateOnly(state.value.startDate);
        applySuggestedStartIfAppropriate(suggestedStart.value);
    }
});

watch(suggestedStart, applySuggestedStartIfAppropriate);
</script>
